"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  GraduationCap,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { faNum, faToman } from "@/lib/format";
import type { Course, CourseLevel } from "./types";

type Props = { onRefresh: () => void };

type FormState = {
  title: string;
  slug: string;
  description: string;
  level: CourseLevel;
  duration: string;
  lessonsCount: string;
  priceToman: string;
  instructor: string;
  launchUrl: string;
  coverImage: string;
  active: boolean;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  title: "",
  slug: "",
  description: "",
  level: "مقدماتی",
  duration: "",
  lessonsCount: "0",
  priceToman: "0",
  instructor: "",
  launchUrl: "",
  coverImage: "",
  active: true,
  sortOrder: "0",
};

const LEVEL_OPTIONS: CourseLevel[] = [
  "مقدماتی",
  "متوسط",
  "پیشرفته",
  "حرفه‌ای",
];

function slugify(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^\w\u0600-\u06FF\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export default function CoursesManager({ onRefresh }: Props) {
  const [courses, setCourses] = React.useState<Course[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Course | null>(null);
  const [form, setForm] = React.useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = React.useState(false);

  const [deleteTarget, setDeleteTarget] = React.useState<Course | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [togglingId, setTogglingId] = React.useState<string | null>(null);

  const loadCourses = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/admin/courses", { cache: "no-store" });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در دریافت دوره‌ها");
      }
      setCourses(data.courses ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در دریافت دوره‌ها";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  }

  function openEdit(c: Course) {
    setEditing(c);
    setForm({
      title: c.title,
      slug: c.slug,
      description: c.description ?? "",
      level: c.level,
      duration: c.duration ?? "",
      lessonsCount: String(c.lessonsCount ?? 0),
      priceToman: String(c.priceToman ?? 0),
      instructor: c.instructor ?? "",
      launchUrl: c.launchUrl ?? "",
      coverImage: c.coverImage ?? "",
      active: c.active,
      sortOrder: String(c.sortOrder ?? 0),
    });
    setFormOpen(true);
  }

  async function submitForm(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("عنوان دوره را وارد کنید.");
      return;
    }
    const slug = form.slug.trim() || slugify(form.title);
    if (!slug) {
      toast.error("نامک (slug) معتبر وارد کنید.");
      return;
    }
    const lessons = Number(form.lessonsCount);
    if (!Number.isFinite(lessons) || lessons < 0) {
      toast.error("تعداد درس‌ها را به عدد وارد کنید.");
      return;
    }
    const price = Number(form.priceToman);
    if (!Number.isFinite(price) || price < 0) {
      toast.error("مبلغ را به عدد وارد کنید.");
      return;
    }
    const sort = Number(form.sortOrder) || 0;

    setSubmitting(true);
    try {
      const body = {
        title: form.title.trim(),
        slug,
        description: form.description.trim() || "",
        level: form.level,
        duration: form.duration.trim() || null,
        lessonsCount: lessons,
        priceToman: price,
        instructor: form.instructor.trim() || null,
        launchUrl: form.launchUrl.trim() || null,
        coverImage: form.coverImage.trim() || null,
        active: form.active,
        sortOrder: sort,
      };
      let res: Response;
      if (editing) {
        res = await fetch("/api/admin/courses", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: editing.id, ...body }),
        });
      } else {
        res = await fetch("/api/admin/courses", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      }
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "خطا در ذخیره دوره");
      }
      toast.success(editing ? "دوره ویرایش شد." : "دوره جدید ساخته شد.");
      setFormOpen(false);
      loadCourses();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در ذخیره دوره";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function toggleActive(c: Course, next: boolean) {
    setTogglingId(c.id);
    try {
      const res = await fetch("/api/admin/courses", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: c.id, active: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در تغییر وضعیت دوره");
      toast.success(next ? "دوره فعال شد." : "دوره غیرفعال شد.");
      loadCourses();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در تغییر وضعیت دوره";
      toast.error(msg);
    } finally {
      setTogglingId(null);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `/api/admin/courses?id=${encodeURIComponent(deleteTarget.id)}`,
        { method: "DELETE" },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "خطا در حذف دوره");
      toast.success("دوره حذف شد.");
      setDeleteTarget(null);
      loadCourses();
      onRefresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "خطا در حذف دوره";
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="border-b">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <GraduationCap className="size-5 text-primary" />
            مدیریت دوره‌ها
          </CardTitle>
          <Button className="bg-primary hover:bg-primary/90" onClick={openCreate}>
            <Plus className="size-4" />
            افزودن دوره
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-destructive">{error}</div>
        ) : courses.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            هنوز دوره‌ای تعریف نشده است.
          </div>
        ) : (
          <div className="max-h-[28rem] overflow-y-auto scrollbar-thin">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead className="pr-4">عنوان دوره</TableHead>
                  <TableHead>سطح</TableHead>
                  <TableHead>مدت</TableHead>
                  <TableHead>درس‌ها</TableHead>
                  <TableHead>مبلغ</TableHead>
                  <TableHead>ثبت‌نام</TableHead>
                  <TableHead>فعال</TableHead>
                  <TableHead className="text-center">عملیات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {courses.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="pr-4">
                      <div className="flex flex-col">
                        <span className="font-medium">{c.title}</span>
                        <span
                          dir="ltr"
                          className="text-xs text-muted-foreground"
                        >
                          {c.slug}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="border-primary/30 text-primary"
                      >
                        {c.level || "—"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.duration ? (
                        c.duration
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {faNum(c.lessonsCount ?? 0)}
                    </TableCell>
                    <TableCell className="text-sm font-semibold text-primary">
                      {c.priceToman === 0 ? (
                        <Badge className="bg-primary/15 text-primary hover:bg-primary/20">
                          رایگان
                        </Badge>
                      ) : (
                        faToman(c.priceToman)
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {faNum(c._count?.enrollments ?? 0)}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={c.active}
                        onCheckedChange={(v) => toggleActive(c, v)}
                        disabled={togglingId === c.id}
                        aria-label="فعال/غیرفعال"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="ویرایش"
                          onClick={() => openEdit(c)}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="حذف"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => setDeleteTarget(c)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create / Edit form dialog */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && setFormOpen(false)}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl scrollbar-thin">
          <DialogHeader>
            <DialogTitle>
              {editing ? "ویرایش دوره" : "افزودن دوره جدید"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "اطلاعات دوره را ویرایش و ذخیره کنید."
                : "یک دوره آموزشی جدید تعریف کنید."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="course-title">عنوان دوره *</Label>
              <Input
                id="course-title"
                placeholder="مثلاً: مقدمات هوش مصنوعی"
                value={form.title}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({
                    ...f,
                    title: v,
                    slug: f.slug || slugify(v),
                  }));
                }}
                disabled={submitting}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="course-slug">نامک (slug)</Label>
              <Input
                id="course-slug"
                dir="ltr"
                placeholder="ai-basics"
                value={form.slug}
                onChange={(e) =>
                  setForm((f) => ({ ...f, slug: slugify(e.target.value) }))
                }
                disabled={submitting}
                className="text-left font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                در صورت خالی بودن، از عنوان تولید می‌شود.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="course-desc">توضیحات</Label>
              <Textarea
                id="course-desc"
                placeholder="توضیح کوتاه درباره دوره..."
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                disabled={submitting}
                className="min-h-16 text-sm"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="course-level">سطح</Label>
                <Select
                  value={form.level}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, level: v as CourseLevel }))
                  }
                  disabled={submitting}
                >
                  <SelectTrigger id="course-level" className="w-full">
                    <SelectValue placeholder="انتخاب سطح" />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVEL_OPTIONS.map((lv) => (
                      <SelectItem key={lv} value={lv}>
                        {lv}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="course-duration">مدت دوره</Label>
                <Input
                  id="course-duration"
                  placeholder="مثلاً: ۱۲ ساعت"
                  value={form.duration}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, duration: e.target.value }))
                  }
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="course-lessons">تعداد درس‌ها</Label>
                <Input
                  id="course-lessons"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.lessonsCount}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      lessonsCount: e.target.value.replace(/[^\d]/g, ""),
                    }))
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="course-price">مبلغ (تومان) — 0 = رایگان</Label>
                <Input
                  id="course-price"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.priceToman}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      priceToman: e.target.value.replace(/[^\d]/g, ""),
                    }))
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="course-instructor">مدرس</Label>
                <Input
                  id="course-instructor"
                  placeholder="نام مدرس"
                  value={form.instructor}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, instructor: e.target.value }))
                  }
                  disabled={submitting}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="course-sort">ترتیب نمایش</Label>
                <Input
                  id="course-sort"
                  dir="ltr"
                  inputMode="numeric"
                  placeholder="0"
                  value={form.sortOrder}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      sortOrder: e.target.value.replace(/[^\d]/g, ""),
                    }))
                  }
                  disabled={submitting}
                  className="text-left"
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="course-launch">آدرس پخش SCORM (اختیاری)</Label>
              <Input
                id="course-launch"
                dir="ltr"
                placeholder="https://..."
                value={form.launchUrl}
                onChange={(e) =>
                  setForm((f) => ({ ...f, launchUrl: e.target.value }))
                }
                disabled={submitting}
                className="text-left font-mono text-sm"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="course-cover">آدرس کاور (اختیاری)</Label>
              <Input
                id="course-cover"
                dir="ltr"
                placeholder="https://..."
                value={form.coverImage}
                onChange={(e) =>
                  setForm((f) => ({ ...f, coverImage: e.target.value }))
                }
                disabled={submitting}
                className="text-left font-mono text-sm"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border bg-muted/40 p-3">
              <div>
                <Label htmlFor="course-active" className="text-sm">
                  فعال
                </Label>
                <p className="text-xs text-muted-foreground">
                  دوره‌های غیرفعال در آکادمی نمایش داده نمی‌شوند.
                </p>
              </div>
              <Switch
                id="course-active"
                checked={form.active}
                onCheckedChange={(v) =>
                  setForm((f) => ({ ...f, active: v }))
                }
                disabled={submitting}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setFormOpen(false)}
                disabled={submitting}
              >
                انصراف
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-primary hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    در حال ذخیره...
                  </>
                ) : editing ? (
                  "ذخیره تغییرات"
                ) : (
                  "ایجاد دوره"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>حذف دوره</AlertDialogTitle>
            <AlertDialogDescription>
              آیا از حذف دوره «{deleteTarget?.title}» مطمئن هستید؟ این عمل قابل
              بازگشت نیست.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>انصراف</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? "در حال حذف..." : "حذف"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
